import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getCurrentUser } from 'aws-amplify/auth';
import PremiumTeaser from '../components/PremiumTeaser';
import { useSubscriptionStatus } from '../hooks/useSubscriptionStatus';
import SocialShare from '../components/SocialShare';
import PaymentGate from '../components/PaymentGate';
import './TravelGroupsPage.css';

const groupCategories = [
  { id: 'all', name: 'All Groups', icon: '🌍' },
  { id: 'family', name: 'Family Trips', icon: '👨‍👩‍👧‍👦' },
  { id: 'friends', name: 'Friends', icon: '👥' },
  { id: 'adventure', name: 'Adventure', icon: '🏔️' },
  { id: 'cultural', name: 'Cultural', icon: '🏛️' },
  { id: 'culinary', name: 'Culinary', icon: '🍜' },
  { id: 'relaxation', name: 'Relaxation', icon: '🏖️' }
];

const mockGroups = [
  {
    id: 1,
    name: "Japan Adventure 2024",
    destination: "Japan",
    dates: "March 15-30, 2024",
    members: 8,
    maxMembers: 12,
    description: "Exploring the best of Japan from Tokyo to Kyoto, including cherry blossom season, traditional temples, and modern city life.",
    organizer: "Sarah Johnson",
    organizerPhoto: "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=50&h=50&fit=crop&crop=face",
    tags: ["Culture", "Food", "Photography"],
    status: "open"
  },
  {
    id: 2,
    name: "European Backpacking",
    destination: "Europe",
    dates: "June 1-30, 2024",
    members: 15,
    maxMembers: 20,
    description: "A month-long backpacking adventure through 8 European countries, staying in hostels and experiencing local culture.",
    organizer: "Michael Chen",
    organizerPhoto: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=50&h=50&fit=crop&crop=face",
    tags: ["Budget", "Adventure", "Backpacking"],
    status: "open"
  },
  {
    id: 3,
    name: "Thailand Beach Retreat",
    destination: "Thailand",
    dates: "April 10-20, 2024",
    members: 6,
    maxMembers: 8,
    description: "Relaxing beach vacation in Thailand with island hopping, snorkeling, and authentic Thai cuisine.",
    organizer: "Emma Rodriguez",
    organizerPhoto: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=50&h=50&fit=crop&crop=face",
    tags: ["Beach", "Relaxation", "Food"],
    status: "full"
  },
  {
    id: 4,
    name: "Peru Trekking Expedition",
    destination: "Peru",
    dates: "July 5-20, 2024",
    members: 10,
    maxMembers: 15,
    description: "Hiking the Inca Trail to Machu Picchu, exploring ancient ruins, and experiencing Andean culture.",
    organizer: "David Kim",
    organizerPhoto: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=50&h=50&fit=crop&crop=face",
    tags: ["Hiking", "Culture", "Adventure"],
    status: "open"
  }
];

const TravelGroupsPage = () => {
  const [groups, setGroups] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [joinedGroups, setJoinedGroups] = useState(new Set());
  const [showPremiumTeaser, setShowPremiumTeaser] = useState(false);
  
  const { subscriptionStatus, isPremium, isFree } = useSubscriptionStatus();

  useEffect(() => {
    fetchGroups();
  }, []);

  async function fetchGroups() {
    try {
      setLoading(true);
      const { userId } = await getCurrentUser();
      setCurrentUser(userId);
      
      // For now, using mock data. In production, this would fetch from the database
      setGroups(mockGroups);
    } catch (err) {
      console.error('Error fetching groups:', err);
    } finally {
      setLoading(false);
    }
  }

  const filteredGroups = groups.filter(group => {
    const matchesFilter = selectedFilter === 'all' || group.status === selectedFilter;
    const matchesSearch = group.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         group.destination.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         group.description.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const handleJoinGroup = (groupId) => {
    if (!currentUser) {
      alert('Please log in to join groups');
      return;
    }
    
    if (!isPremium) {
      setShowPremiumTeaser(true);
      return;
    }
    
    setJoinedGroups(prev => new Set([...prev, groupId]));
    // In production, this would make an API call to join the group
  };

  const handleCreateGroup = () => {
    if (!isPremium) {
      setShowPremiumTeaser(true);
      return;
    }
    
    setShowCreateForm(true);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getDaysUntil = (dateString) => {
    const today = new Date();
    const tripDate = new Date(dateString);
    const diffTime = tripDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  if (loading) {
    return (
      <div className="travel-groups-page">
        <div className="loading">
          <div className="spinner"></div>
          <p>Loading amazing travel groups...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="travel-groups-page">
      <h1>Travel Together, Explore More and Be Safe</h1>
      <div className="travel-groups-intro card p-6" style={{ background: '#f8fafc', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-sm)', marginBottom: 'var(--spacing-4)' }}>
        <p style={{ fontSize: '1.1em' }}>
          Discover the joy of group travel! Join or create a travel group with people in the UK, Europe, and beyond. GradedWash matches you with compatible companions based on your personality, so you can travel with friends who truly get you. Group travel means more fun, more memories, and greater peace of mind.
        </p>
        <p style={{ marginTop: 'var(--spacing-2)' }}>
          <strong>How does it work?</strong> As part of signing up, you'll take our scientific personality quiz. Your results help us match you with the most compatible travel companions and friends. The better the match, the more enjoyable and safe your journey!
        </p>
        <p style={{ marginTop: 'var(--spacing-2)' }}>
          <strong>Find your next adventure—join a travel group or start your own today!</strong>
        </p>
      </div>
      <PaymentGate feature="travel-groups" userSubscriptionStatus={subscriptionStatus}>
        <button className="create-group-btn" onClick={handleCreateGroup}>Start a New Travel Group</button>
      </PaymentGate>
      <div className="groups-list">
        {/* Filters */}
        <div className="groups-controls">
          <div className="search-box">
            <input
              type="text"
              placeholder="Search groups by destination or name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
          
          <div className="category-filters">
            <button
              onClick={() => setSelectedFilter('all')}
              className={`category-button ${selectedFilter === 'all' ? 'active' : ''}`}
            >
              <span className="category-icon">{groupCategories.find(cat => cat.id === 'all')?.icon}</span>
              <span className="category-name">{groupCategories.find(cat => cat.id === 'all')?.name}</span>
            </button>
            <button
              onClick={() => setSelectedFilter('open')}
              className={`category-button ${selectedFilter === 'open' ? 'active' : ''}`}
            >
              <span className="category-icon">{groupCategories.find(cat => cat.id === 'open')?.icon}</span>
              <span className="category-name">{groupCategories.find(cat => cat.id === 'open')?.name}</span>
            </button>
            <button
              onClick={() => setSelectedFilter('full')}
              className={`category-button ${selectedFilter === 'full' ? 'active' : ''}`}
            >
              <span className="category-icon">{groupCategories.find(cat => cat.id === 'full')?.icon}</span>
              <span className="category-name">{groupCategories.find(cat => cat.id === 'full')?.name}</span>
            </button>
          </div>
        </div>

        {/* Groups Grid */}
        {filteredGroups.map((group) => {
          const daysUntil = getDaysUntil(group.dates);
          const isJoined = joinedGroups.has(group.id);
          const isFull = group.members >= group.maxMembers;
          
          return (
            <div key={group.id} className="group-card">
              <div className="group-header">
                <div className="group-category">
                  {groupCategories.find(cat => cat.id === group.status)?.icon}
                  {groupCategories.find(cat => cat.id === group.status)?.name}
                </div>
                <div className="group-status">
                  {isFull ? (
                    <span className="status-badge full">Full</span>
                  ) : (
                    <span className="status-badge available">Open</span>
                  )}
                </div>
              </div>

              <div className="group-content">
                <h3 className="group-name">{group.name}</h3>
                <p className="group-description">{group.description}</p>
                
                <div className="group-destination">
                  <span className="destination-icon">📍</span>
                  <span className="destination-text">{group.destination}</span>
                </div>

                <div className="group-dates">
                  <div className="date-range">
                    <span className="date-icon">📅</span>
                    <span className="date-text">
                      {formatDate(group.dates)}
                    </span>
                  </div>
                  {daysUntil > 0 && (
                    <div className="days-until">
                      <span className="days-icon">⏰</span>
                      <span className="days-text">{daysUntil} days until departure</span>
                    </div>
                  )}
                </div>

                <div className="group-participants">
                  <div className="participants-info">
                    <span className="participants-icon">👥</span>
                    <span className="participants-text">
                      {group.members}/{group.maxMembers} members
                    </span>
                  </div>
                  <div className="participants-bar">
                    <div 
                      className="participants-fill"
                      style={{ width: `${(group.members / group.maxMembers) * 100}%` }}
                    ></div>
                  </div>
                </div>

                <div className="group-tags">
                  {group.tags.slice(0, 4).map(tag => (
                    <span key={tag} className="group-tag">#{tag}</span>
                  ))}
                </div>
              </div>

              <div className="group-actions">
                {isJoined ? (
                  <button className="btn btn-success" disabled>
                    ✓ Joined
                  </button>
                ) : isFull ? (
                  <button className="btn btn-secondary" disabled>
                    Group Full
                  </button>
                ) : (
                  <button 
                    className="btn btn-primary"
                    onClick={() => handleJoinGroup(group.id)}
                  >
                    Join Group
                  </button>
                )}
                {/* Social Share for this group */}
                <div style={{ margin: '0.5em 0' }}>
                  <SocialShare
                    url={window.location.origin + '/travel-groups'}
                    title={`Join me on ${group.name}!`}
                    summary={`${group.description} - ${group.destination}`}
                    hashtag="#TravelGroups"
                  />
                </div>
                <Link to={`/groups/${group.id}`} className="btn btn-outline">
                  View Details
                </Link>
              </div>
            </div>
          );
        })}
      </div>

      {filteredGroups.length === 0 && (
        <div className="empty-state">
          <div className="empty-icon">👥</div>
          <h3>No groups found</h3>
          <p>Try adjusting your search or create the first group!</p>
          <button 
            className="btn btn-primary"
            onClick={handleCreateGroup}
          >
            Create Group
          </button>
        </div>
      )}

      {/* Create Group Form */}
      {showCreateForm && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Create New Travel Group</h3>
              <button 
                className="modal-close"
                onClick={() => setShowCreateForm(false)}
              >
                ×
              </button>
            </div>
            
            <div className="modal-body">
              <p>This feature will be available soon! You'll be able to create groups for:</p>
              <ul>
                <li>Family trips with other families</li>
                <li>Friend groups looking for more travelers</li>
                <li>Solo travelers wanting to join existing groups</li>
                <li>Special interest groups (hiking, food, culture, etc.)</li>
              </ul>
              <p>Groups will include messaging, planning tools, and social sharing features.</p>
            </div>
            
            <div className="modal-footer">
              <button 
                className="btn btn-secondary"
                onClick={() => setShowCreateForm(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Social Sharing Section */}
      <div className="social-section">
        <h3>Share Your Travel Plans</h3>
        <p>Let friends and family know about your upcoming adventures!</p>
        <SocialShare
          url={window.location.href}
          title="Join me on GradedWash - Find your perfect travel companions!"
          summary="Discover amazing travel groups and connect with like-minded travelers. Perfect for families, friends, and solo adventurers."
          hashtag="#TravelGroups"
        />
      </div>

      {/* Premium Teaser Modal */}
      <PremiumTeaser 
        feature="travel-groups"
        showModal={showPremiumTeaser}
        onClose={() => setShowPremiumTeaser(false)}
      />
    </div>
  );
};

export default TravelGroupsPage; 